<script setup>
import { computed, getCurrentInstance, inject } from 'vue'
import { storeToRefs } from 'pinia'

import { useAuthStore } from '@/stores/auth.js'
import { useAppInfo } from '@/composables/appInfo'

const isFeatureEnabled = inject('isFeatureEnabled')

// TODO: Remove this once a full migration to Vue 3 is done.
const { proxy } = getCurrentInstance()

const authStore = useAuthStore()
const appInfo = useAppInfo()

const { userData, isPartnerAccount } = storeToRefs(authStore)

const isPvPartner = computed(() => {
  return isPartnerAccount.value && isFeatureEnabled('VANTAGE_PARTNER')
})

const showDocumentation = () => {
  if (isPvPartner.value) {
    window.open('https://partner-docs.megaport.com/services/creating-estimates/', '_blank')
  } else {
    window.open('https://docs.megaport.com/finance/creating-estimates/', '_blank')
  }
}

const fullName = computed(() => {
  return userData.value.firstName && userData.value.lastName
    ? `${userData.value.firstName} ${userData.value.lastName}`
    : userData.value.fullName
})

const userInitials = computed(() => {
  if (userData.value.firstName && userData.value.lastName) {
    return `${userData.value.firstName[0]}${userData.value.lastName[0]}`
  } else if (userData.value.fullName) {
    const splitName = userData.value.fullName.split(' ')

    if (splitName.length > 1) {
      return `${splitName[0][0]}${splitName[1][0]}`
    }

    return userData.value.fullName.slice(0, 2)
  }

  // TODO: Need this to avoid an error on logout occurring due to data being reset before a redirect to the login screen happens. Investigate refactoring the logout process.
  return ''
})

const logOut = async () => {
  try {
    await authStore.logOut()
  } finally {
    proxy.$router.push('/login', () => {})
  }
}
</script>

<template>
  <div>
    <header
      v-if="$route.meta.requiresLogin"
      class="menu-header">
      <!-- App Logo -->
      <RouterLink
        to="/"
        class="menu-logo">
        <img
          src="@/assets/images/megaport-logo.svg"
          :alt="$t('general.megaport-logo')"
          width="132"
          height="31" />
        <el-tooltip
          :content="appInfo"
          :open-delay="500">
          <div class="app-name">{{ $t('app-info.name-short') }}</div>
        </el-tooltip>
      </RouterLink>

      <!-- Actions "Menu" -->
      <el-menu
        mode="horizontal"
        class="menu-bar">
        <!-- Documentation -->
        <el-menu-item @click="showDocumentation">
          {{ $t('general.documentation') }}
        </el-menu-item>

        <!-- User "Sub-Menu" -->
        <el-submenu
          id="userProfileMenu"
          index="user"
          popper-class="mr-1"
          class="user-profile-menu"
          :aria-label="$t('menu.user-menu')">
          <!-- User Initials -->
          <template #title>
            <el-button circle>
              {{ userInitials }}
            </el-button>
          </template>

          <!-- User Account Info -->
          <div class="user-profile-menu__header">
            <div class="user-profile-menu__header-name">
              {{ fullName }}
            </div>
            <div class="user-profile-menu__header-email">
              {{ userData.email }}
            </div>
          </div>

          <!-- Log Out -->
          <el-menu-item @click="logOut">
            {{ $t('authentication.log-out') }}
          </el-menu-item>
        </el-submenu>
      </el-menu>
    </header>

    <RouterView />
  </div>
</template>

<style lang="scss">
svg {
  display: block; // Gets rid of the annoying space around the icons
}

.el-loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.menu-header {
  z-index: 1000;
  position: sticky;
  top: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-inline: 2rem;
  background-color: #fff;
}

.menu-bar {
  display: flex;
  align-items: center;
  border-bottom: 0 !important;
}

.menu-logo {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  text-decoration: none;

  .app-name {
    align-self: flex-end;
    line-height: 1rem;
    font-size: 1.1rem;
    color: var(--color-text-100);
  }
}

// TODO: When migrating to Vue 3, the 'submenu' class and component was changed to 'sub-menu'
.user-profile-menu {
  > .el-submenu__title {
    > .el-submenu__icon-arrow {
      display: none;
    }

    .el-menu:not(.el-menu--collapse) & {
      padding-inline: 0;
    }
  }

  .el-button.is-circle {
    padding: 0;
    width: 40px;
    height: 40px;
    vertical-align: baseline;
  }
}

.user-profile-menu__header {
  padding: 10px;
  border-bottom: 1px solid var(--color-text-10);
}

.user-profile-menu__header-name {
  font-size: 1.8rem;
  font-weight: 700;
}

.user-profile-menu__header-email {
  font-size: 1.4rem;
  font-weight: 300;
  color: var(--color-text-80);
}
</style>
